import type { ReactNode } from 'react';

import type { GetComponentsContextProps, SanityComponentProps } from '~/types/sanity/components';

import { Divider } from '~/components/atoms/Divider';
import { AccordionSanity } from '~/components/molecules/AccordionSanity';
import { CTA } from '~/components/molecules/CTA';
import { ImageSanityWrapper } from '~/components/molecules/ImageSanityWrapper';
import { BrandCarousel } from '~/components/organisms/BrandCarousel';
import { CardList } from '~/components/organisms/CardList';
import { FAQList } from '~/components/organisms/FAQList';
import { FeaturedIconPoints } from '~/components/organisms/FeaturedIconPoints';
import { Hero } from '~/components/organisms/Hero';
import { ImageText } from '~/components/organisms/ImageText';
import { LoyaltyAboutRichText } from '~/components/organisms/LoyaltyAboutRichText';
import { People } from '~/components/organisms/People';
import { Testimonials } from '~/components/organisms/Testimonials';
import { TextContent } from '~/components/organisms/TextContent';
import { Usps } from '~/components/organisms/Usps';
import { VideoText } from '~/components/organisms/VideoText';
import type { SanityLoyaltyAboutComponentProps } from '~/types/sanity/loyalty';

export const getComponents = (
  components: SanityComponentProps[],
  contextProps?: GetComponentsContextProps,
): ReactNode[] => {
  return components.map(({ _key, ...props }) => {
    switch (props._type) {
      case 'cardList':
        return <CardList key={_key} {...props} {...contextProps} />;
      case 'cta':
        return <CTA key={_key} {...props} {...contextProps} />;
      case 'faqList':
        return <FAQList key={_key} {...props} />;
      case 'hero':
        return <Hero key={_key} {...props} {...contextProps} />;
      case 'people':
        return <People key={_key} {...props} />;
      case 'testimonials':
        return <Testimonials key={_key} _key={_key} {...props} />;
      case 'imageText':
        return <ImageText key={_key} {...props} {...contextProps} />;
      case 'brandCarousel':
        return <BrandCarousel key={_key} _key={_key} {...props} />;
      case 'usps':
        return <Usps key={_key} {...props} />;
      case 'featuredIconPoints':
        return <FeaturedIconPoints key={_key} {...props} />;
      case 'videoText':
        return <VideoText key={_key} {...props} />;
      case 'textContent':
        return <TextContent key={_key} {...props} />;
      default:
        return null;
    }
  });
};

/**
 * Get the components for the loyalty about route
 * @param components - The components data from the Sanity loyalty about route
 * @returns - An array of React components
 */
export const getLoyaltyComponents = (components: SanityLoyaltyAboutComponentProps[]): ReactNode[] => {
  return components.map(({ _key, ...props }) => {
    switch (props._type) {
      case 'responsiveImage':
        return <ImageSanityWrapper key={_key} {...props} />;
      case 'loyaltyAboutRichText':
        return <LoyaltyAboutRichText key={_key} {...props} />;
      case 'accordion':
        return <AccordionSanity key={_key} {...props} />;
      case 'divider':
        return <Divider key={_key} {...props} />;
      default:
        return null;
    }
  });
};
